import React, { useState, useRef, useEffect } from "react";
import { FiGlobe } from "react-icons/fi";
import StableLanguageIcon from "./StableLanguageIcon";
import { SUPPORTED_LANGUAGES, getLanguageInfo } from "../constants/languages";

const PostLanguageSelector = ({
  selectedLanguage,
  onLanguageChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedLang = getLanguageInfo(selectedLanguage);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLanguageSelect = (language) => {
    onLanguageChange(language.value);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="mb-6">
      <label className="flex items-center text-gray-700 font-medium mb-2">
        <FiGlobe className="mr-2 text-blue-600" aria-hidden="true" />
        Ngôn ngữ bài viết
      </label>

      <div className="relative" ref={dropdownRef}>
        {/* Dropdown Button */}
        <button
          type="button"
          onClick={toggleDropdown}
          disabled={disabled}
          className={`
            w-full px-4 py-3 bg-white border border-gray-300 rounded-lg
            focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            flex items-center justify-between
            transition-all duration-200 ease-in-out
            ${
              disabled
                ? "opacity-50 cursor-not-allowed bg-gray-50"
                : "hover:border-blue-400 hover:shadow-sm cursor-pointer"
            }
            ${isOpen ? "border-blue-500 ring-2 ring-blue-500" : ""}
          `}
        >
          <div className="flex items-center">
            <StableLanguageIcon
              code={selectedLang.value}
              label={selectedLang.label}
              size="lg"
              className="mr-3"
            />
            <span className="font-medium text-gray-700">
              {selectedLang.label}
            </span>
          </div>

          {/* Dropdown Arrow */}
          <svg
            className={`
              w-5 h-5 text-gray-400 transition-transform duration-200
              ${isOpen ? "transform rotate-180" : ""}
            `}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {isOpen && !disabled && (
          <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            <div className="max-h-64 overflow-y-auto">
              {SUPPORTED_LANGUAGES.map((language, index) => (
                <button
                  key={language.value}
                  type="button"
                  onClick={() => handleLanguageSelect(language)}
                  className={`
                    w-full px-4 py-3 text-left flex items-center
                    transition-colors duration-150 ease-in-out
                    hover:bg-blue-50 hover:text-blue-700
                    ${
                      selectedLanguage === language.value
                        ? "bg-blue-50 text-blue-700 border-r-4 border-blue-500"
                        : "text-gray-700"
                    }
                    ${
                      index !== SUPPORTED_LANGUAGES.length - 1
                        ? "border-b border-gray-100"
                        : ""
                    }
                  `}
                >
                  <StableLanguageIcon
                    code={language.value}
                    label={language.label}
                    size="lg"
                    className="mr-3"
                  />
                  <span className="font-medium">{language.label}</span>

                  {/* Check icon for selected language */}
                  {selectedLanguage === language.value && (
                    <svg
                      className="w-5 h-5 ml-auto text-blue-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default PostLanguageSelector;
